import type React from 'react';
import { ActionIcon, Box, Flex, Textarea } from '@mantine/core';
import { IconCheck, IconSend, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { TEXTAREA_MAX_ROWS } from '@/constants/ui';
import type { CommentInputProps } from '../types';
import {
  COMMENT_ACTION_BUTTON_ICON_SIZE,
  COMMENT_INPUT_MIN_ROWS,
} from '../constants';
import styles from './CommentInput.module.css';

export const CommentInput = ({
  value,
  onChange,
  onSend,
  onCancel,
  isEditing = false,
  disabled = false,
  placeholder,
}: CommentInputProps) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (value.trim()) {
        onSend();
      }
    }
    if (event.key === 'Escape' && isEditing && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <Box className={styles.inputWrapper}>
      <Flex align="flex-end" gap="xs">
        <Textarea
          placeholder={
            placeholder ||
            (isEditing ? t('comments.editMessage') : t('comments.writeComment'))
          }
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          autosize
          minRows={COMMENT_INPUT_MIN_ROWS}
          maxRows={TEXTAREA_MAX_ROWS}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          classNames={{
            root: styles.textareaRoot,
            input: styles.textareaInput,
          }}
        />

        {isEditing && onCancel ? (
          <>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onCancel}
              className={styles.actionButton}
              aria-label={t('common.cancel')}
            >
              <IconX size={COMMENT_ACTION_BUTTON_ICON_SIZE} />
            </ActionIcon>
            <ActionIcon
              variant="subtle"
              onClick={onSend}
              disabled={disabled || !value.trim()}
              className={styles.actionButton}
              aria-label={t('common.save')}
            >
              <IconCheck size={COMMENT_ACTION_BUTTON_ICON_SIZE} />
            </ActionIcon>
          </>
        ) : (
          <ActionIcon
            variant="subtle"
            onClick={onSend}
            disabled={disabled || !value.trim()}
            className={styles.actionButton}
            aria-label={t('common.send')}
          >
            <IconSend
              size={COMMENT_ACTION_BUTTON_ICON_SIZE}
              className={styles.sendIcon}
            />
          </ActionIcon>
        )}
      </Flex>
    </Box>
  );
};
